import { lazy, Suspense, useEffect, useId, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '@uiw/react-md-editor/markdown-editor.css';

import { Switch } from '@domcrm/embed-ui';
import { postAuthMeFeedback, type AuthMeFeedbackPayload } from '../widget-api/auth-me';
import { ApiError } from '../widget-api/http-client';
import { useEmbedBootstrap } from '../use-widget-bootstrap';
import { useQuerySuffix } from '../widget-hooks/useQuerySuffix';
import { PATH } from '../widget-lib/path-routes';

const MAX_BODY_LEN = 1000;

const MDEditor = lazy(async () => {
  const mod = await import('@uiw/react-md-editor/nohighlight');
  return { default: mod.default };
});

const DEFAULT_FORM: AuthMeFeedbackPayload = {
  recipient: 'developers',
  category: 'improvement',
  body_md: '',
  is_anonymous: false
};

function feedbackErrorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.detail;
  if (err instanceof Error) return err.message;
  return 'Не удалось отправить';
}

export type EmbedFeedbackButtonProps = {
  /** Компактная кнопка в футере или вторичный вариант. */
  variant?: 'footer' | 'inline';
};

/**
 * Кнопка «Обратная связь»: диалог по мотивам StaffContactDialog, POST /api/v1/auth/me/feedback.
 * Без JWT ведёт на вход (клиент или тренерский контур).
 */
export function EmbedFeedbackButton({ variant = 'footer' }: EmbedFeedbackButtonProps) {
  const formId = useId();
  const location = useLocation();
  const navigate = useNavigate();
  const suffix = useQuerySuffix();
  const { accessToken } = useEmbedBootstrap();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<AuthMeFeedbackPayload>(DEFAULT_FORM);
  const [bodyError, setBodyError] = useState<string | undefined>();
  const [submitError, setSubmitError] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);
  const [successNote, setSuccessNote] = useState<string | null>(null);

  const [colorMode, setColorMode] = useState<'light' | 'dark'>(() =>
    document.documentElement.classList.contains('dark') ? 'dark' : 'light'
  );

  useEffect(() => {
    const check = () =>
      setColorMode(document.documentElement.classList.contains('dark') ? 'dark' : 'light');
    const obs = new MutationObserver(check);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  const trainerContour = location.pathname === PATH.trainer || location.pathname.startsWith(`${PATH.trainer}/`);
  const signInPath = trainerContour ? PATH.trainerSignIn : PATH.signIn;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  function close(): void {
    setOpen(false);
    setForm(DEFAULT_FORM);
    setBodyError(undefined);
    setSubmitError(undefined);
    setSubmitting(false);
    setSuccessNote(null);
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!accessToken) {
      navigate(`${signInPath}${suffix}`, { state: { from: location.pathname + location.search } });
      return;
    }
    const text = form.body_md.trim();
    if (!text) {
      setBodyError('Введите текст сообщения');
      return;
    }
    if (text.length > MAX_BODY_LEN) {
      setBodyError(`Не более ${MAX_BODY_LEN} символов`);
      return;
    }
    setBodyError(undefined);
    setSubmitError(undefined);
    setSubmitting(true);
    try {
      await postAuthMeFeedback(accessToken, { ...form, body_md: text });
      setSuccessNote('Сообщение отправлено. Спасибо!');
      setTimeout(() => close(), 1600);
    } catch (err) {
      setSubmitError(feedbackErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  function openDialog(): void {
    if (!accessToken) {
      navigate(`${signInPath}${suffix}`, { state: { from: location.pathname + location.search } });
      return;
    }
    setSubmitError(undefined);
    setSuccessNote(null);
    setOpen(true);
  }

  const btnClass =
    variant === 'footer'
      ? 'rounded-sm px-2 py-1 text-[11px] text-[var(--text-muted)] underline decoration-[var(--border)] underline-offset-2 hover:text-[var(--text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg-elevated)]'
      : 'rounded-sm text-sm text-[var(--accent)] underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg-elevated)]';

  if (!accessToken) return null;

  return (
    <>
      <button type="button" className={btnClass} onClick={() => openDialog()}>
        Обратная связь
      </button>
      {open ? (
        <div
          className="fixed inset-0 z-[100] flex items-end justify-center bg-black/50 p-3 sm:items-center"
          role="presentation"
          onClick={() => !submitting && close()}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={`${formId}-title`}
            className="max-h-[min(88vh,560px)] w-full max-w-md overflow-y-auto rounded-sm border border-[var(--border)] bg-[var(--bg-elevated)] p-4 text-left shadow-lg sm:p-5"
            onClick={(ev) => ev.stopPropagation()}
          >
            <h2 id={`${formId}-title`} className="text-base font-semibold text-[var(--text)]">
              Обратная связь
            </h2>
            {successNote ? (
              <p className="mt-4 text-sm text-emerald-700 dark:text-emerald-400" role="status">
                {successNote}
              </p>
            ) : (
              <form id={formId} className="mt-4 space-y-3" onSubmit={(ev) => void onSubmit(ev)}>
                <div>
                  <label className="mb-1 block text-[13px] text-[var(--text-muted)]" htmlFor={`${formId}-cat`}>
                    Тема
                  </label>
                  <select
                    id={`${formId}-cat`}
                    className="h-9 w-full rounded-sm border border-[var(--border)] bg-[var(--bg)] px-2 text-sm text-[var(--text)] outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
                    value={form.category}
                    onChange={(ev) =>
                      setForm((p) => ({ ...p, category: ev.target.value as AuthMeFeedbackPayload['category'] }))
                    }
                  >
                    <option value="improvement">Предложение улучшения</option>
                    <option value="complaint">Жалоба</option>
                    <option value="freeform">Свободная форма</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-[13px] text-[var(--text-muted)]" htmlFor={`${formId}-rec`}>
                    Адресат
                  </label>
                  <select
                    id={`${formId}-rec`}
                    className="h-9 w-full rounded-sm border border-[var(--border)] bg-[var(--bg)] px-2 text-sm text-[var(--text)] outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
                    value={form.recipient}
                    onChange={(ev) =>
                      setForm((p) => ({ ...p, recipient: ev.target.value as AuthMeFeedbackPayload['recipient'] }))
                    }
                  >
                    <option value="developers">Платформа</option>
                    <option value="org_admin">Администратор организации</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-[13px] text-[var(--text-muted)]" htmlFor={`${formId}-body`}>
                    Текст (можно Markdown)
                  </label>
                  <div
                    data-color-mode={colorMode}
                    className={bodyError ? '[&_.w-md-editor]:ring-1 [&_.w-md-editor]:ring-[var(--error-text)]' : ''}
                  >
                    <Suspense
                      fallback={
                        <div className="flex min-h-[140px] items-center justify-center rounded-sm border border-[var(--border)] bg-[var(--bg-soft)] text-sm text-[var(--text-muted)]">
                          Загрузка редактора…
                        </div>
                      }
                    >
                      <MDEditor
                        value={form.body_md}
                        onChange={(v) => {
                          setForm((p) => ({ ...p, body_md: v ?? '' }));
                          if (bodyError) setBodyError(undefined);
                        }}
                        preview="edit"
                        visibleDragbar={false}
                        height={200}
                        highlightEnable={false}
                        commandsFilter={(command) => {
                          const n = command.name;
                          if (n && ['code', 'codeBlock', 'image', 'comment', 'help'].includes(n)) return false;
                          return command;
                        }}
                        textareaProps={{
                          id: `${formId}-body`,
                          placeholder: 'Опишите вопрос или предложение…',
                          spellCheck: true,
                        }}
                      />
                    </Suspense>
                  </div>
                  <div className="mt-1 flex flex-col gap-0.5 text-[10px] text-[var(--text-muted)]">
                    {bodyError ? <span className="text-[var(--error-text)]">{bodyError}</span> : null}
                    <span>{form.body_md.length}/{MAX_BODY_LEN}</span>
                  </div>
                </div>
                <label className="flex items-center justify-between gap-3 rounded-sm border border-[var(--border)] bg-[var(--bg-soft)] px-3 py-2">
                  <span className="text-sm text-[var(--text)]">Отправить анонимно</span>
                  <Switch
                    checked={form.is_anonymous}
                    onCheckedChange={(v) => setForm((p) => ({ ...p, is_anonymous: v }))}
                    aria-label="Отправить анонимно"
                  />
                </label>
                {submitError ? <p className="text-xs text-[var(--error-text)]">{submitError}</p> : null}
                <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row">
                  <button
                    type="button"
                    className="h-9 rounded-sm border border-[var(--border)] bg-[var(--bg)] px-3 text-sm text-[var(--text)] hover:bg-[var(--bg-soft)] disabled:opacity-50"
                    disabled={submitting}
                    onClick={close}
                  >
                    Закрыть
                  </button>
                  <button
                    type="submit"
                    className="h-9 rounded-sm bg-[var(--btn-primary-bg)] px-3 text-sm font-medium text-[var(--btn-primary-fg)] hover:bg-[var(--btn-primary-bg-hover)] disabled:opacity-50"
                    disabled={submitting}
                  >
                    {submitting ? 'Отправка…' : 'Отправить'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      ) : null}
    </>
  );
}
